import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import FFileUploadV2 from 'components/BaseComponents/FFileUploadV2'
import SBox from 'components/opinionless/SBox'
import SText from 'components/opinionless/SText'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import { SStyled, useSTheme } from 'styles/theme'
import { useMultipleDocumentsUploadMutation } from 'types/graphql'
import { ga4_useTitle } from 'utils/ga4'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import ConditionalRender from 'utils/conditionalRender'

const LBTextHeader = SStyled(SText)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 24px;
            line-height: 130%;
        }
    `}
`

interface Props {
    setCurrentStep?: (number) => void
    fontFamily: any
    nextStep?: any
    onPrevious?: any
    updatePercentage: any
    selectedDocument?: any
    disableBackButton?: boolean
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

const DocumentsProofOfAddressUpload: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const theme = useSTheme()
    const form = useForm()
    const { t } = useTranslation(window.LANG_NS)
    const applicationContext = useContext(ApplicationContext)
    const [currentDocument, setCurrentDocument] = useState(null)
    const [uploaded, setUploaded] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [documentCategory, setDocumentCategory] = useState('')

    useEffect(() => {
        setDocumentCategory(props.selectedDocument)
    }, [props.selectedDocument])

    ga4_useTitle('Household income')

    const fontFamily = props.fontFamily

    const [uploadDocMutation, { loading }] = useMultipleDocumentsUploadMutation()

    const { refetch } = useApplicationBasicInfo()
    const {
        loading: applicationAnswersLoading,
        refetch: refetchApplicationAnswers,
        getAnswersByCategory,
    } = useApplicationAnswers()

    useEffect(() => {
        if (!documentCategory) {
            const contextSelectedDocument = getAnswersByCategory('PROOF_OF_RESIDENCY_CATEGORY')?.[0]
            if (contextSelectedDocument) {
                setDocumentCategory(contextSelectedDocument.response)
            }
        }
    }, [applicationContext.answers])

    const onNext = async () => {
        const documentType = 'PROOF_OF_RESIDENCY'

        setIsUploading(true)

        if (uploaded) {
            await uploadDocMutation({
                variables: {
                    files: [currentDocument],
                    personId: applicationContext?.profile?.person?.id,
                    documentType: documentType,
                },
            })
                .then(() => {
                    refetch()
                })
                .finally(() => {
                    setIsUploading(false)
                    setUploaded(false)
                    props.nextStep()
                })
        } else {
            setIsUploading(false)
            props.nextStep()
        }
    }

    const onUpload = async (file) => {
        setCurrentDocument(file)
        setUploaded(true)
    }

    useEffect(() => {
        if (applicationContext?.documents?.length) {
            setCurrentDocument(
                applicationContext?.documents.find(
                    (itm) =>
                        itm.node.person.id === applicationContext.profile.person.id &&
                        itm.node.name &&
                        itm.node.documentType === 'PROOF_OF_RESIDENCY'
                )?.node
            )
        }
    }, [applicationContext?.documents])

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.documents,
        WIZARD_STEPS_MAPPING.documents.proofOfAddressUpload,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    const proofOfResidenceDocumentAnswer = t(`${getAnswersByCategory('PROOF_OF_RESIDENCY_CATEGORY')?.[0]?.response}`)

    return (
        <FormProvider {...form}>
            <Container>
                <SBox
                    as="form"
                    onSubmit={(e) => {
                        e.preventDefault()

                        onNext()
                    }}
                >
                    <FButtonPrevious onClick={props.onPrevious} />
                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.secondaryColor,
                            fontFamily: fontFamily.header,
                        }}
                    >
                        {t('long_beach_documents_poa_upload_header')}
                    </LBTextHeader>

                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: theme.colors.black,
                            fontSize: '14px',
                            width: '250px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_documents_poa_upload_description'),
                        }}
                    />
                    <LBText
                        sx={{
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            color: theme.colors.gray400,
                            fontSize: '14px',
                            border: '1.623px solid #CCC',
                            background: '#F2F2F2',
                            padding: '13px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t(documentCategory) || proofOfResidenceDocumentAnswer,
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: theme.colors.gray400,
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_documents_poa_upload_warning_description'),
                        }}
                    />
                    <FFileUploadV2
                        onUpload={onUpload}
                        defaultDocument={currentDocument ? [currentDocument] : null}
                        ref={form.register({ required: 'required_field' as string })}
                        name={'proof_of_income_upload_file'}
                        id={'proof_of_income_upload_file'}
                        multiple={false}
                        isUploading={isUploading}
                        canBeRemoved={false}
                    />

                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            marginTop: '0 !important',
                            lineHeight: '140%',
                            color: theme.colors.gray400,
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_documents_poa_upload_footer'),
                        }}
                    />
                    <LBButton
                        type={'submit'}
                        id={'continueButton'}
                        disabled={!currentDocument}
                        loading={loading}
                        title={t('save_and_continue')}
                        width={'100%'}
                        backgroundColor={!currentDocument ? 'grey' : theme.colors.lbBlue}
                        mt={7}
                        mb={5}
                        textStyle={{
                            fontWeight: 700,
                            fontSize: '14px',
                            lineHeight: '140%',
                            fontFamily: fontFamily.body,
                            color: theme.colors.white,
                        }}
                    >
                        {t('save_and_continue')}
                    </LBButton>
                </SBox>
            </Container>
        </FormProvider>
    )
}

export default DocumentsProofOfAddressUpload
